import { useState } from "react";
import { ArrowLeft, Plus, CreditCard, Building2 } from "lucide-react";
import { BottomNav } from "./bottom-nav";
import {
  PaymentCardItem,
  BankAccountItem,
  AddCardModal,
  AddBankAccountModal,
} from "./payment-methods";
import type { PaymentCard, BankAccount } from "./payment-methods";

interface PaymentMethodsPageProps {
  onBack: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export function PaymentMethodsPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: PaymentMethodsPageProps) {
  // Local UI state until integrated with a payments API (Stripe Customer / etc.).
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);

  // Modal States
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [isAddBankModalOpen, setIsAddBankModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<PaymentCard | null>(null);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);

  // Card Handlers
  const handleAddCard = (card: Omit<PaymentCard, "id">) => {
    const newCard: PaymentCard = {
      ...card,
      id: Date.now().toString(),
    };

    // If this card is set as default, unset others
    if (newCard.isDefault) {
      setCards(cards.map((c) => ({ ...c, isDefault: false })));
    }

    setCards([...cards, newCard]);
    alert("Cartão adicionado com sucesso!");
  };

  const handleEditCard = (card: PaymentCard) => {
    setEditingCard(card);
    setIsAddCardModalOpen(true);
  };

  const handleUpdateCard = (updatedCard: Omit<PaymentCard, "id">) => {
    if (!editingCard) return;

    // If this card is set as default, unset others
    if (updatedCard.isDefault) {
      setCards(cards.map((c) => ({ ...c, isDefault: false })));
    }

    setCards(
      cards.map((c) =>
        c.id === editingCard.id ? { ...updatedCard, id: c.id } : c
      )
    );
    setEditingCard(null);
    alert("Cartão atualizado com sucesso!");
  };

  const handleDeleteCard = (id: string) => {
    if (confirm("Deseja remover este cartão?")) {
      setCards(cards.filter((c) => c.id !== id));
      alert("Cartão removido com sucesso!");
    }
  };

  const handleSetDefaultCard = (id: string) => {
    setCards(
      cards.map((c) => ({
        ...c,
        isDefault: c.id === id,
      }))
    );
    alert("Cartão padrão atualizado!");
  };

  // Bank Account Handlers
  const handleAddBankAccount = (account: Omit<BankAccount, "id">) => {
    const newAccount: BankAccount = {
      ...account,
      id: Date.now().toString(),
    };

    // If this account is set as default, unset others
    if (newAccount.isDefault) {
      setBankAccounts(bankAccounts.map((a) => ({ ...a, isDefault: false })));
    }

    setBankAccounts([...bankAccounts, newAccount]);
    alert("Conta bancária adicionada com sucesso!");
  };

  const handleEditBankAccount = (account: BankAccount) => {
    setEditingAccount(account);
    setIsAddBankModalOpen(true);
  };

  const handleUpdateBankAccount = (updatedAccount: Omit<BankAccount, "id">) => {
    if (!editingAccount) return;

    // If this account is set as default, unset others
    if (updatedAccount.isDefault) {
      setBankAccounts(bankAccounts.map((a) => ({ ...a, isDefault: false })));
    }

    setBankAccounts(
      bankAccounts.map((a) =>
        a.id === editingAccount.id ? { ...updatedAccount, id: a.id } : a
      )
    );
    setEditingAccount(null);
    alert("Conta bancária atualizada com sucesso!");
  };

  const handleDeleteBankAccount = (id: string) => {
    if (confirm("Deseja remover esta conta bancária?")) {
      setBankAccounts(bankAccounts.filter((a) => a.id !== id));
      alert("Conta bancária removida com sucesso!");
    }
  };

  const handleSetDefaultBankAccount = (id: string) => {
    setBankAccounts(
      bankAccounts.map((a) => ({
        ...a,
        isDefault: a.id === id,
      }))
    );
    alert("Conta bancária padrão atualizada!");
  };

  const handleCloseCardModal = () => {
    setIsAddCardModalOpen(false);
    setEditingCard(null);
  };

  const handleCloseBankModal = () => {
    setIsAddBankModalOpen(false);
    setEditingAccount(null);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Métodos de Pagamento</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {/* Payment Cards Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-medium">Cartões para Compras</h2>
                <p className="text-sm text-muted-foreground">
                  Use para pagar suas compras
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsAddCardModalOpen(true)}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              aria-label="Adicionar cartão"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-3">
            {cards.length > 0 ? (
              cards.map((card) => (
                <PaymentCardItem
                  key={card.id}
                  card={card}
                  onEdit={handleEditCard}
                  onDelete={handleDeleteCard}
                  onSetDefault={handleSetDefaultCard}
                />
              ))
            ) : (
              <div className="bg-card rounded-xl border border-border p-8 text-center">
                <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">
                  Nenhum cartão cadastrado
                </p>
                <button
                  onClick={() => setIsAddCardModalOpen(true)}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Adicionar Primeiro Cartão
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Bank Accounts Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-medium">Minhas Contas Bancárias</h2>
                <p className="text-sm text-muted-foreground">
                  Receba o valor das suas vendas
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsAddBankModalOpen(true)}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              aria-label="Adicionar conta bancária"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-3">
            {bankAccounts.length > 0 ? (
              bankAccounts.map((account) => (
                <BankAccountItem
                  key={account.id}
                  account={account}
                  onEdit={handleEditBankAccount}
                  onDelete={handleDeleteBankAccount}
                  onSetDefault={handleSetDefaultBankAccount}
                />
              ))
            ) : (
              <div className="bg-card rounded-xl border border-border p-8 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">
                  Nenhuma conta bancária cadastrada
                </p>
                <button
                  onClick={() => setIsAddBankModalOpen(true)}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Adicionar Primeira Conta
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Security Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-medium text-blue-900 mb-2">
            Seus dados estão seguros
          </h3>
          <p className="text-sm text-blue-800">
            Todas as informações de pagamento são criptografadas e armazenadas de
            forma segura. Nunca compartilhamos seus dados financeiros.
          </p>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />

      {/* Modals */}
      <AddCardModal
        isOpen={isAddCardModalOpen}
        onClose={handleCloseCardModal}
        onSave={editingCard ? handleUpdateCard : handleAddCard}
        editingCard={editingCard}
      />

      <AddBankAccountModal
        isOpen={isAddBankModalOpen}
        onClose={handleCloseBankModal}
        onSave={editingAccount ? handleUpdateBankAccount : handleAddBankAccount}
        editingAccount={editingAccount}
      />
    </div>
  );
}